// Analytics tracking service
import { API } from '../utils/api';

// Generate or retrieve unique visitor ID
function getVisitorId() {
  let visitorId = localStorage.getItem('sa_visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('sa_visitor_id', visitorId);
  }
  return visitorId;
}

// Get session ID (resets after 30 min of inactivity)
function getSessionId() {
  const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
  let session = JSON.parse(sessionStorage.getItem('sa_session') || '{}');
  const now = Date.now();
  
  if (!session.id || (now - session.lastActivity) > SESSION_TIMEOUT) {
    session = {
      id: 's_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 6),
      lastActivity: now
    };
  } else {
    session.lastActivity = now;
  }
  
  sessionStorage.setItem('sa_session', JSON.stringify(session));
  return session.id;
}

// Capturar UTMs de la URL al cargar; persistir 24h en sessionStorage.
function getUtmParams() {
  try {
    const KEY = 'sa_utm';
    const TTL = 24 * 60 * 60 * 1000;
    const url = new URL(window.location.href);
    const fresh = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach((k) => {
      const v = url.searchParams.get(k);
      if (v) fresh[k] = v;
    });
    if (Object.keys(fresh).length > 0) {
      sessionStorage.setItem(KEY, JSON.stringify({ ts: Date.now(), data: fresh }));
      return fresh;
    }
    const stored = JSON.parse(sessionStorage.getItem(KEY) || 'null');
    if (stored && Date.now() - (stored.ts || 0) < TTL) return stored.data || {};
  } catch (e) { /* noop */ }
  return {};
}

// Mapa de eventos internos → eventos de GA4 (key events / conversions)
// Permite que GTM/GA4 reciba los nombres estándar que se usan en la industria.
const GA4_EVENT_MAP = {
  // Lead generation (KEY EVENT en GA4)
  quote_submitted: { ga4: 'generate_lead', is_conversion: true },
  cotizacion_submitted: { ga4: 'generate_lead', is_conversion: true },
  contact_sent: { ga4: 'contact_form_submit', is_conversion: true },
  contact_form: { ga4: 'contact_form_submit', is_conversion: true },
  denuncia_submitted: { ga4: 'generate_lead', is_conversion: true },
  // Tool engagement (NO conversion, sí engagement)
  reto21_start: { ga4: 'reto21_start', is_conversion: false },
  reto21_complete: { ga4: 'reto21_complete', is_conversion: true },
  calculator_start: { ga4: 'calculator_start', is_conversion: false },
  calculator_complete: { ga4: 'calculator_complete', is_conversion: true },
  terapeutia_session_start: { ga4: 'terapeutia_start', is_conversion: false },
  // Contact intents
  whatsapp_click: { ga4: 'whatsapp_click', is_conversion: true },
  call_click: { ga4: 'phone_click', is_conversion: true },
  phone_reveal: { ga4: 'phone_reveal', is_conversion: false },
  maps_click: { ga4: 'maps_click', is_conversion: false },
};

function _pushDataLayer(eventType, targetType, targetId, extra) {
  try {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    const mapping = GA4_EVENT_MAP[eventType];
    if (!mapping) return; // Solo emitir eventos mapeados para no contaminar GA4
    window.dataLayer.push({
      event: mapping.ga4,
      target_type: targetType || undefined,
      target_id: targetId || undefined,
      conversion: mapping.is_conversion || false,
      ...(extra || {}),
    });
  } catch (e) { /* never break UX */ }
}

export async function trackEvent(eventType, targetType = null, targetId = null, pagePath = null) {
  // GA4 / GTM push (sin bloquear)
  _pushDataLayer(eventType, targetType, targetId);
  // Backend tracking (analytics_events collection en MongoDB)
  try {
    await fetch(API + '/analytics/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        event_type: eventType,
        target_type: targetType,
        target_id: targetId,
        page_path: pagePath || window.location.pathname,
        referrer: document.referrer || null,
        visitor_id: getVisitorId(),
        session_id: getSessionId(),
        ...getUtmParams(),
      })
    });
  } catch (e) {
    // Silent fail - don't break UX for analytics
    console.debug('Analytics track failed:', e);
  }
}

// Track page view
export function trackPageView(targetType = null, targetId = null) {
  trackEvent('page_view', targetType, targetId);
}

// Track WhatsApp click
export function trackWhatsAppClick(targetType, targetId) {
  trackEvent('whatsapp_click', targetType, targetId);
}

// Track call click
export function trackCallClick(targetType, targetId) {
  trackEvent('call_click', targetType, targetId);
}

// Track phone reveal (user clicked "Ver Número")
export function trackPhoneReveal(targetType, targetId) {
  trackEvent('phone_reveal', targetType, targetId);
}

// Track maps click
export function trackMapsClick(targetType, targetId) {
  trackEvent('maps_click', targetType, targetId);
}

// Track contact form sent
export function trackContactSent(targetType, targetId) {
  trackEvent('contact_sent', targetType, targetId);
}

// Track contact form - more specific
export function trackContactForm(targetType, targetId) {
  trackEvent('contact_form', targetType, targetId);
}

